<template>
  <div class="product-detail-page">
    <!-- 返回按钮 -->
    <div class="back-button" @click="goBack">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
      </svg>
      <span>返回</span>
    </div>

    <!-- 产品详情内容 -->
    <div v-if="product" class="product-detail-container">
      <!-- 产品详情展示区 -->
      <div class="product-details-section">
        <h2 class="section-subtitle">产品详情</h2>
        
        <!-- 产品详情图片网格 -->
        <!-- 加载状态 -->
        <div v-if="loadingImages" class="loading-container">
          <div class="loading-spinner"></div>
          <p>加载详情图片中...</p>
        </div>
        
        <!-- 错误状态 -->
        <div v-else-if="errorImages" class="error-container">
          <p>加载详情图片失败: {{ errorImages }}</p>
        </div>
        
        <!-- 详情图片 - 上半部分 -->
        <div v-else-if="detailImages.length > 0" class="detail-images-grid">
          <div v-for="(image, index) in detailImages.slice(0, Math.ceil(detailImages.length / 2))" :key="image.id || index" class="detail-image-item">
            <img :src="getImageUrl(image)" :alt="product.name + ' 详情图' + (index + 1)" />
          </div>
        </div>
      </div>

      <!-- 产品信息区 - 放在页面中间 -->
      <div class="product-info-section">
        <h1 class="product-title">{{ product.name }}</h1>
        <p class="product-price">{{ product.price }}</p>
        <p class="product-description">{{ product.description }}</p>
        
        <!-- 产品功能标签 -->
        <div class="product-features">
          <div class="feature-item">
            <span class="feature-icon">⚡</span>
            <span class="feature-text">快速充电</span>
          </div>
          <div class="feature-item">
            <span class="feature-icon">🔌</span>
            <span class="feature-text">多重保护</span>
          </div>
          <div class="feature-item">
            <span class="feature-icon">🌍</span>
            <span class="feature-text">全球通用</span>
          </div>
          <div class="feature-item">
            <span class="feature-icon">🔧</span>
            <span class="feature-text">易于安装</span>
          </div>
        </div>

        <!-- 行动按钮 -->
        <div class="action-buttons">
          <button class="contact-button">联系我们</button>
          <button class="inquiry-button">咨询详情</button>
        </div>
      </div>

      <!-- 产品详情展示区 - 下半部分 -->
      <div class="product-details-section">
        <!-- 详情图片 - 下半部分 -->
        <div v-if="detailImages.length > 0" class="detail-images-grid">
          <div v-for="(image, index) in detailImages.slice(Math.ceil(detailImages.length / 2))" :key="image.id || index" class="detail-image-item">
            <img :src="getImageUrl(image)" :alt="product.name + ' 详情图' + (index + Math.ceil(detailImages.length / 2) + 1)" />
          </div>
        </div>
        
        <!-- 无详情图片状态 -->
        <div v-if="!loadingImages && !errorImages && detailImages.length === 0" class="empty-container">
          <p>暂无产品详情图片</p>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-else class="loading-container">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();

// 产品数据
const product = ref<any>(null);
const category = ref<string>('');

// 产品详情图片数据
const detailImages = ref<any[]>([]);
const loadingImages = ref<boolean>(false);
const errorImages = ref<string>('');

// 返回上一页
const goBack = () => {
  router.back();
};

// 获取当前语言
const currentLanguage = computed(() => locale.value);

// 从URL参数中获取产品数据
onMounted(() => {
  try {
    const productStr = route.query.product as string;
    const categoryStr = route.query.category as string;
    if (productStr) {
      product.value = JSON.parse(productStr);
      category.value = categoryStr || '';
      // 获取产品详情图片
      if (product.value.id) {
        fetchProductDetailImages(product.value.id);
      }
    }
  } catch (error) {
    console.error('解析产品数据失败:', error);
  }
});

// 监听语言变化，更新产品详情图片
import { watch } from 'vue';
watch(
  () => locale.value,
  (newLocale) => {
    // 语言变化时，不需要重新获取数据，直接切换显示的图片字段
    console.log('Language changed to:', newLocale);
  }
);

// 获取产品详情图片数据
const fetchProductDetailImages = async (productId: number) => {
  if (!productId) return;
  
  loadingImages.value = true;
  errorImages.value = '';
  
  try {
    const response = await fetch(`/jeecg-boot/energycube/topicImg/queryDetail?id=${productId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // 检查响应代码
    if (data.code === 200) {
      // 按sort字段排序图片
      detailImages.value = data.result.sort((a: any, b: any) => a.sort - b.sort);
    } else {
      throw new Error(data.message || '获取产品详情图片失败');
    }
  } catch (err) {
    errorImages.value = err instanceof Error ? err.message : '未知错误';
    console.error('获取产品详情图片失败:', err);
  } finally {
    loadingImages.value = false;
  }
};

// 根据当前语言获取图片URL
const getImageUrl = (image: any) => {
  if (!image) return '';
  // 根据当前语言选择不同的图片字段
  return currentLanguage.value === 'zh-Hant' ? image.imgUrl : image.imgUrlen;
};

</script>

<style scoped>
.product-detail-page {
  max-width: 1200px;
  margin: 80px auto 0;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* 返回按钮 */
.back-button {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  cursor: pointer;
  color: #333;
  transition: color 0.3s ease;
}

.back-button:hover {
  color: #007bff;
}

.back-button svg {
  margin-right: 8px;
}

.back-button span {
  font-size: 16px;
}

/* 产品详情容器 */
.product-detail-container {
  display: flex;
  flex-direction: column;
  gap: 60px;
}

/* 主图展示区 */
.product-main-image-section {
  width: 100%;
}

.main-image-container {
  position: relative;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  background-color: #f8f8f8;
}

.main-image {
  width: 100%;
  height: auto;
  max-height: 600px;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.main-image:hover {
  transform: scale(1.02);
}

/* 标签样式 */
.new-badge, .hot-badge {
  position: absolute;
  top: 20px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: bold;
  color: #fff;
}

.new-badge {
  background-color: #e74c3c;
  left: 20px;
}

.hot-badge {
  background-color: #f39c12;
  right: 20px;
}

/* 产品信息区 */
.product-info-section {
  text-align: center;
}

.product-title {
  font-size: 32px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
}

.product-price {
  font-size: 28px;
  font-weight: bold;
  color: #e74c3c;
  margin-bottom: 20px;
}

.product-description {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto 40px;
}

/* 产品功能标签 */
.product-features {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 40px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: #f8f8f8;
  border-radius: 25px;
  font-size: 14px;
  color: #555;
}

.feature-icon {
  font-size: 18px;
}

/* 行动按钮 */
.action-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.contact-button, .inquiry-button {
  padding: 15px 40px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.contact-button {
  background-color: #333;
  color: #fff;
}

.contact-button:hover {
  background-color: #000;
  transform: translateY(-2px);
}

.inquiry-button {
  background-color: #fff;
  color: #333;
  border: 2px solid #333;
}

.inquiry-button:hover {
  background-color: #f8f8f8;
  transform: translateY(-2px);
}

/* 产品详情展示区 */
.product-details-section {
  margin-top: 40px;
}

.section-subtitle {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 30px;
  text-align: center;
}

/* 详情图片网格 - 单列布局 */
.detail-images-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 60px;
}

.detail-image-item {
  border-radius: 12px;
  overflow: hidden;
  background-color: transparent;
}

.detail-image-item img {
  width: 100%;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s ease;
  max-width: 100%;
}

.detail-image-item img:hover {
  transform: scale(1.05);
}

/* 产品规格 */
.product-specifications {
  background-color: #f8f8f8;
  padding: 40px;
  border-radius: 12px;
}

.specifications-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 25px;
}

.specification-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;
}

.specification-item:last-child {
  border-bottom: none;
}

.spec-label {
  font-weight: 500;
  color: #555;
}

.spec-value {
  color: #333;
}

/* 加载状态 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #333;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .product-detail-page {
    padding: 15px;
  }
  
  .product-title {
    font-size: 24px;
  }
  
  .product-price {
    font-size: 22px;
  }
  
  .detail-images-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .detail-image-item img {
    height: 250px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 15px;
  }
  
  .product-features {
    gap: 10px;
  }
  
  .product-specifications {
    padding: 20px;
  }
}
</style>